/**
 * One figure, its name, and what it is measured against.
 *
 * The value arrives as a string and leaves as a string. Nothing here adds,
 * subtracts or parses: the server measured it, and a screen that recomputed it
 * would eventually disagree with the ledger about what the account holds.
 *
 * A gain and a loss differ by a sign and an arrow, not only by colour. Roughly
 * one man in twelve cannot separate the two colours this product uses for
 * money, and he still has to be able to tell a profit from a loss.
 *
 * The size is asked for as a role — the one number a region is about, a
 * second-rank figure, a cell in a strip — never as a size. That is what keeps
 * eight regions from each choosing their own idea of "big".
 */
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

import {
  isNegative,
  isZero,
  money,
  signedMoney,
  toPlaces,
  type Money,
} from "@/shared/money/money";
import {
  metric,
  metricTestId,
  type MetricId,
  type MetricRole,
} from "@/shared/metric/catalog";

/** What rank this figure holds in its region. */
export type MetricSize = "display" | "large" | "normal";

const SIZE: Record<MetricSize, string> = {
  display: "text-hero-metric",
  large: "text-metric-md",
  normal: "text-body-main",
};

const GLYPH: Record<MetricSize, string> = {
  display: "size-6",
  large: "size-5",
  normal: "size-4",
};

type CommonProps = {
  readonly value: Money | null;
  /** Show a sign and a direction. For results, never for balances. */
  readonly directional?: boolean;
  readonly hint?: string | undefined;
  readonly size?: MetricSize;
  /** Rendered instead of the money formatting, for figures that are not money. */
  readonly display?: string | undefined;
  /**
   * Whether the figure brings its own label and hint, or only the number.
   *
   * `figure` is for the one place the contract already gives a figure a label:
   * a lead-strip cell (§4.1) renders the caption and the second line itself,
   * and a metric that printed its own would put the same words on the cell
   * twice. Everything else about the figure — the unit, the sign, the arrow,
   * the exact value on hover, the metric identity the gates read — is
   * unchanged, because those belong to the figure and not to its container.
   */
  readonly chrome?: "full" | "figure";
};

type RegisteredMetricProps = {
  readonly metricId: MetricId;
  readonly role: MetricRole;
  readonly label?: never;
  readonly testId?: never;
};

type UnregisteredMetricProps = {
  readonly label: string;
  readonly testId?: string;
  /**
   * What the figure is counted in, when no catalog entry says so.
   *
   * Registered metrics take it from the catalog; a one-off has to name it or
   * go without. `null` is the honest answer for a ratio or a count — a unit
   * invented to fill the slot is worse than none.
   */
  readonly unit?: string | null;
  readonly metricId?: never;
  readonly role?: never;
};

type Props = CommonProps & (RegisteredMetricProps | UnregisteredMetricProps);

export function Metric(props: Props): React.JSX.Element {
  const {
    value,
    directional = false,
    hint,
    size = "normal",
    display,
    chrome = "full",
  } = props;
  const label =
    props.metricId === undefined ? props.label : metric(props.metricId).label;
  const testId =
    props.metricId === undefined
      ? (props.testId ?? `figure-${label}`)
      : metricTestId(props.metricId);
  // Read the direction from the rounded figure, not the exact one: a loss too
  // small to show must not colour the line red and point an arrow down at what
  // reads as a zero.
  const unavailable = value === null;
  const shown = unavailable ? null : toPlaces(value);
  const negative = shown === null ? false : isNegative(shown);
  const zero = shown === null ? false : isZero(shown);
  const Arrow = zero ? Minus : negative ? ArrowDownRight : ArrowUpRight;

  const tone = unavailable
    ? "text-text-secondary"
    : !directional
    ? "text-text-primary"
    : zero
      ? "text-text-secondary"
      : negative
        ? "text-status-negative"
        : "text-status-positive";

  // The unit, quietly, beside the figure. The rule used to be "state the
  // currency once per region", and on a strip of six balances that left six
  // bare numbers under a heading nobody reads twice — a reader landing on the
  // middle of the strip could not tell USDT from a percentage. It is set small
  // and muted so it never competes with the figure it qualifies.
  const unit =
    props.metricId === undefined
      ? (props.unit ?? null)
      : metric(props.metricId).unit === "money"
        ? "USDT"
        : null;

  return (
    <div className="flex min-w-0 flex-col">
      {/* Monospace, like the figure under it. The mock sets caption and value
          in one face so the pair reads as one instrument; a proportional
          caption over a monospace number makes them look like two systems
          sharing a panel. */}
      {chrome === "full" && (
        <span className="mb-0.5 text-label-caps text-text-muted">
          {label}
        </span>
      )}
      <span className={`flex items-baseline gap-1 font-numeric ${SIZE[size]} ${tone}`}>
        {/* No glyph on a zero. The dash meant "unchanged", but set against
            0.00 it reads as a minus sign — a reader seeing "— 0.00" has been
            told the figure is negative, which is the one thing it is not. A
            zero carries neither sign nor colour, and now neither glyph. */}
        {directional && !unavailable && !zero && (
          <Arrow aria-hidden="true" className={`${GLYPH[size]} shrink-0 self-center`} />
        )}
        <span
          data-testid={testId}
          data-metric-id={props.metricId}
          data-metric-role={props.role}
          // The exact figure, one hover away. Two decimals is what a person
          // reads; the whole of it is what the account holds, and it stays
          // reachable rather than being quietly dropped.
          title={value ?? undefined}
          // Never truncated. A figure clipped to "+72…" is not a smaller
          // figure, it is a different one, and the reader has no way to know
          // which. If it does not fit, the layout is wrong — so it wraps or
          // pushes, and the wrongness is visible instead of hidden.
          className="whitespace-nowrap"
        >
          {unavailable ? "—" : (display ?? (directional ? signedMoney(value) : money(value)))}
        </span>
        {unit !== null && !unavailable && (
          <span className="shrink-0 text-helper-sm font-normal text-text-muted">{unit}</span>
        )}
      </span>
      {chrome === "full" && hint !== undefined && (
        <span className="text-helper-sm text-text-muted">{hint}</span>
      )}
    </div>
  );
}

/**
 * A row of figures sharing one ground.
 *
 * One panel holding six metrics, not six panels holding one each. The figures
 * are related — they describe one account at one moment — and a border between
 * every pair says they are not.
 */
export function KpiStrip({
  children,
  columns = 5,
}: {
  readonly children: React.ReactNode;
  readonly columns?: 3 | 4 | 5 | 6;
}): React.JSX.Element {
  const wide: Record<number, string> = {
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
  };
  return (
    <div className={`grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 ${wide[columns] ?? ""}`}>
      {children}
    </div>
  );
}
